import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Space, Modal, Tooltip } from 'antd';
import { EditOutlined, InfoCircleOutlined, MedicineBoxOutlined, ThunderboltOutlined } from '@ant-design/icons';
import './ToothChartChild.css';
import HtmlParser from 'react-html-parser';
import { _newLine } from '@/utils/helpers';
import { TOOTH_ACTIONS } from './helpers';

const toothNumbers = [
    '55', '54', '53', '52', '51', '61', '62', '63', '64', '65',
    '85', '84', '83', '82', '81', '71', '72', '73', '74', '75',
];

const initialValues = Object.fromEntries(toothNumbers.map(num => [num, '0']));

const TOOTH_ACTION_MAP = TOOTH_ACTIONS.reduce((map, item) => {
    map[item.value] = item;
    return map;
}, {});

const getToothSrcPublic = (num) => `/child_tooth_images/${num}.png`;

const parseActions = (item) => {
    if (Array.isArray(item.actions)) return item.actions;

    if (item.actions_json) {
        try {
            const parsed = JSON.parse(item.actions_json);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }

    if (item.status && item.status !== 'none') {
        return [item.status];
    }

    return [];
};

const ToothChartChild = ({ onDataChange, data }) => {
    const [values, setValues] = useState(initialValues);
    const [selected, setSelected] = useState([]);
    const [toothActions, setToothActions] = useState(
        Object.fromEntries(toothNumbers.map(n => [n, []]))
    );
    const [deletedTeeth, setDeletedTeeth] = useState([]);
    const [notes, setNotes] = useState(
        Object.fromEntries(toothNumbers.map(n => [n, '']))
    );
    const [noteModal, setNoteModal] = useState({
        open: false,
        num: null,
        value: '',
    });

    const isFirstLoad = useRef(true);

    useEffect(() => {
        if (data && Array.isArray(data)) {
            const newValues = { ...initialValues };
            const newActions = Object.fromEntries(toothNumbers.map(n => [n, []]));
            const newDeleted = [];
            const newNotes = Object.fromEntries(toothNumbers.map(n => [n, '']));

            data.forEach(item => {
                const num = String(item.tooth_number);

                if (toothNumbers.includes(num)) {
                    newValues[num] = item.tooth_size || '0';
                    newActions[num] = parseActions(item);
                    newNotes[num] = item.note || '';

                    if (item.is_deleted) {
                        newDeleted.push(num);
                    }
                }
            });

            setValues(newValues);
            setToothActions(newActions);
            setDeletedTeeth(newDeleted);
            setNotes(newNotes);
            isFirstLoad.current = true;
        }
    }, [data]);

    useEffect(() => {
        if (isFirstLoad.current) {
            isFirstLoad.current = false;
            return;
        }

        if (onDataChange) {
            onDataChange(prepareDataForSave());
        }
    }, [values, toothActions, deletedTeeth, notes]);

    const prepareDataForSave = () => {
        return toothNumbers.map(num => ({
            tooth_number: num,
            tooth_size: values[num] || '0',
            actions: toothActions[num] || [],
            actions_json: JSON.stringify(toothActions[num] || []),
            status: toothActions[num]?.[0] || 'none',
            note: notes[num] || '',
            is_deleted: deletedTeeth.includes(num) ? 1 : 0,
            tooth_type: 'children',
        }));
    };

    const toggleSelect = (num) => {
        setSelected(prev =>
            prev.includes(num)
                ? prev.filter(n => n !== num)
                : [...prev, num]
        );
    };

    const applyAction = (actionValue) => {
        if (selected.length === 0) return;

        setToothActions(prev => {
            const next = { ...prev };

            selected.forEach(num => {
                const current = next[num] || [];

                next[num] = current.includes(actionValue)
                    ? current.filter(item => item !== actionValue)
                    : [...current, actionValue];
            });

            return next;
        });
    };

    const handleAction = (action) => {
        switch (action) {
            case 'delete':
                if (selected.length === 0) return;
                setDeletedTeeth(prev => Array.from(new Set([...prev, ...selected])));
                setSelected([]);
                break;

            case 'reset':
                if (selected.length === 0) return;

                setValues(prev => {
                    const next = { ...prev };
                    selected.forEach(num => {
                        next[num] = '0';
                    });
                    return next;
                });

                setToothActions(prev => {
                    const next = { ...prev };
                    selected.forEach(num => {
                        next[num] = [];
                    });
                    return next;
                });

                setDeletedTeeth(prev => prev.filter(num => !selected.includes(num)));

                setNotes(prev => {
                    const next = { ...prev };
                    selected.forEach(num => {
                        next[num] = '';
                    });
                    return next;
                });

                setSelected([]);
                break;

            case 'selectAll':
                setSelected(toothNumbers);
                break;

            case 'deselect':
                setSelected([]);
                break;

            default:
                applyAction(action);
                break;
        }
    };

    const openNote = (num) => {
        setNoteModal({
            open: true,
            num,
            value: notes[num] || '',
        });
    };

    const saveNote = () => {
        const { num, value } = noteModal;

        if (!num) return;

        setNotes(prev => ({
            ...prev,
            [num]: value,
        }));

        setNoteModal({
            open: false,
            num: null,
            value: '',
        });
    };

    const renderTooth = (num) => {
        const actions = toothActions[num] || [];
        const isShapeHidden = deletedTeeth.includes(num);
        const imgSrc = getToothSrcPublic(num);
        const note = notes[num];
        const hasActions = actions.length > 0;

        return (
            <Tooltip
                key={num}
                title={note ? HtmlParser(note) : null}
                placement="top"
                mouseEnterDelay={0.3}
            >
                <div
                    className={`child-tooth ${selected.includes(num) ? 'selected' : ''} ${isShapeHidden ? 'child-tooth-deleted' : ''}`}
                    onClick={() => toggleSelect(num)}
                >
                    <div className="child-tooth-number-wrapper">
                        <div className="child-tooth-number">{num}</div>

                        {note && (
                            <span
                                className="child-tooth-note-indicator"
                                onClick={(e) => e.stopPropagation()}
                            />
                        )}
                    </div>

                    <div className="child-tooth-action-zone">
                        {hasActions ? (
                            <div className="child-tooth-action-badges">
                                {actions.map(actionValue => {
                                    const action = TOOTH_ACTION_MAP[actionValue];

                                    return (
                                        <div
                                            key={actionValue}
                                            className="child-tooth-action-badge"
                                            style={{
                                                background: action?.color || '#64748b',
                                            }}
                                        >
                                            {action?.label || actionValue}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="child-tooth-action-empty" />
                        )}
                    </div>

                    <div className="child-tooth-image-box">
                        {isShapeHidden ? (
                            <div className="child-tooth-image-placeholder" />
                        ) : (
                            <img
                                src={imgSrc}
                                alt={`Tooth ${num}`}
                                className="child-tooth-image"
                                draggable={false}
                            />
                        )}
                    </div>

                    <div className="child-tooth-footer">
                        <Tooltip title={note ? 'Sửa ghi chú' : 'Thêm ghi chú'}>
                            <Button
                                size="small"
                                className={`child-note-btn ${note ? 'has-note' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openNote(num);
                                }}
                            >
                                <EditOutlined />
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            </Tooltip>
        );
    };

    return (
        <div className="child-tooth-chart-wrapper">
            <div className="child-tooth-medical-header">
                <div className="child-tooth-title-block">
                    <div className="child-tooth-title-icon">
                        <MedicineBoxOutlined />
                    </div>

                    <div>
                        <div className="child-tooth-medical-title">Sơ đồ răng trẻ em</div>
                        <div className="child-tooth-medical-desc">
                            Chọn một hoặc nhiều răng sữa để gán tình trạng, chỉ định điều trị và ghi chú lâm sàng.
                        </div>
                    </div>
                </div>

                <div className="child-tooth-selected-card">
                    <div className="child-selected-label">Đang chọn</div>
                    <div className="child-selected-value">
                        {selected.length}
                        <span> răng</span>
                    </div>
                    <div className="child-selected-list">
                        {selected.length > 0 ? selected.join(', ') : 'Chưa chọn răng'}
                    </div>
                </div>
            </div>

            <div className="child-tooth-grid">
                <div className="child-tooth-row child-top-row">
                    {toothNumbers.slice(0, 10).map(num => renderTooth(num))}
                </div>

                <div className="child-tooth-row child-bottom-row">
                    {toothNumbers.slice(10).map(num => renderTooth(num))}
                </div>
            </div>

            <div className="child-tooth-medical-panel">
                <div className="child-tooth-toolbar-section child-quick-section">
                    <div className="child-toolbar-title">
                        <ThunderboltOutlined /> Thao tác nhanh
                    </div>

                    <Space wrap>
                        <Button onClick={() => handleAction('selectAll')}>
                            Chọn tất cả
                        </Button>

                        <Button onClick={() => handleAction('deselect')}>
                            Bỏ chọn
                        </Button>

                        <Button danger onClick={() => handleAction('delete')}>
                            Đánh dấu mất răng
                        </Button>

                        <Button onClick={() => handleAction('reset')}>
                            Reset răng
                        </Button>
                    </Space>
                </div>

                <div className="child-tooth-toolbar-section child-action-section">
                    <div className="child-toolbar-title">
                        <MedicineBoxOutlined /> Tình trạng / chỉ định điều trị
                    </div>

                    <Space wrap>
                        {TOOTH_ACTIONS.map(action => (
                            <Button
                                key={action.value}
                                className="child-tooth-action-button"
                                style={{
                                    background: action.color,
                                    borderColor: action.color,
                                }}
                                onClick={() => applyAction(action.value)}
                            >
                                {action.label}
                            </Button>
                        ))}
                    </Space>
                </div>

                <div className="child-tooth-help-box">
                    <InfoCircleOutlined />
                    <span>
                        Nhấn vào răng để chọn hoặc bỏ chọn.
                        Sau đó chọn tình trạng bên dưới để gán hoặc hủy cho các răng đã chọn.
                        Có thể reset từng răng hoặc reset toàn bộ về trạng thái ban đầu.
                    </span>
                </div>
            </div>

            <Modal
                open={noteModal.open}
                title={`Ghi chú cho răng ${noteModal.num}`}
                onCancel={() => setNoteModal({ open: false, num: null, value: '' })}
                onOk={saveNote}
                okText="Lưu"
                cancelText="Hủy"
            >
                <Input.TextArea
                    rows={10}
                    value={_newLine(noteModal.value)}
                    onChange={(e) =>
                        setNoteModal(prev => ({
                            ...prev,
                            value: e.target.value,
                        }))
                    }
                    placeholder="Nhập ghi chú lâm sàng cho răng này..."
                />
            </Modal>
        </div>
    );
};

export default ToothChartChild;